import React from "react";
import {
  Box,
  HStack,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import type { TadbirFund } from "../../hooks/useFunds";
import { getFundLogoSrc } from "./fundCardUtils";

const HIDDEN_FUND_REGISTER_CODES = new Set(["11339", "11530"]);

type FundSelectionOption = {
  value: string;
  title: string;
  caption: React.ReactNode;
  logoSrc?: string | null;
  fallback: string;
};

type FundSelectionCardProps = {
  fundData: TadbirFund | TadbirFund[];
  isActive?: boolean;
  onClick?: () => void;
  minHeight?: string | number;
  logoSize?: string | number;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
};

export const FundSelectionCard: React.FC<FundSelectionCardProps> = ({
  fundData,
  isActive = false,
  onClick,
  minHeight = "85px",
  logoSize = "85px",
  value,
  onChange,
  placeholder = "انتخاب صندوق",
}) => {
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const activeBorderColor = useColorModeValue("brand.500", "brand.300");
  const hoverBorderColor = useColorModeValue("brand.300", "brand.400");
  const cardBg = useColorModeValue("gray.50", "dark.900");
  const menuBg = useColorModeValue("white", "dark.800");
  const menuHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const titleColor = useColorModeValue("gray.800", "white");
  const captionColor = useColorModeValue("gray.500", "gray.400");
  const fallbackBg = useColorModeValue("whiteAlpha.700", "whiteAlpha.100");
  const selectorBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const selectorColor = useColorModeValue("brand.600", "brand.200");
  const selectorBorderColor = useColorModeValue("brand.100", "brand.700");
  const isHiddenFund = (fund: TadbirFund) =>
    HIDDEN_FUND_REGISTER_CODES.has(String(fund.fundRegisterCode || "").trim());

  const toOption = (fund: TadbirFund): FundSelectionOption => {
    const fallback = String(fund.fundRegisterCode || "—");

    return {
      value: String(fund.nav?.seoRegisterNo || fallback),
      title: fund.fundName?.trim() || fallback,
      caption: fund.fundTypeTitle?.trim() || fallback,
      logoSrc: getFundLogoSrc(fund.fundRegisterCode),
      fallback,
    };
  };

  const renderCard = (
    card: Omit<FundSelectionOption, "value">,
    active: boolean,
    showChevron = false,
    compact = false,
  ) => (
    <Box
      w="100%"
      minH={compact ? "68px" : minHeight}
      px={compact ? 3 : 4}
      py={compact ? 2 : 4}
      borderRadius={compact ? "xl" : "2xl"}
      borderWidth="1px"
      borderColor={active ? activeBorderColor : borderColor}
      bg={cardBg}
      transition="all 0.2s ease"
      _hover={{ borderColor: hoverBorderColor }}
      dir="rtl"
    >
      <HStack spacing={compact ? 2.5 : 3} align="center">
        <Box
          w={compact ? "52px" : logoSize}
          h={compact ? "52px" : logoSize}
          borderRadius={compact ? "lg" : "xl"}
          display="flex"
          alignItems="center"
          justifyContent="center"
          overflow="hidden"
          flexShrink={0}
        >
          {card.logoSrc ? (
            <Image
              src={card.logoSrc}
              alt={card.title}
              maxW="100%"
              maxH="100%"
              objectFit="contain"
            />
          ) : (
            <Box
              w="100%"
              h="100%"
              borderRadius="xl"
              bg={fallbackBg}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text color={captionColor} fontWeight="bold" fontSize="xs">
                {card.fallback}
              </Text>
            </Box>
          )}
        </Box>

        <VStack align="start" spacing={compact ? 0.5 : 1} flex="1" minW={0}>
          <Text
            fontWeight="bold"
            whiteSpace="normal"
            textAlign="right"
            color={titleColor}
            fontSize={compact ? "sm" : undefined}
            lineHeight={compact ? "short" : undefined}
          >
            {card.title}
          </Text>
          <Text fontSize="xs" color={captionColor} textAlign="right">
            {card.caption}
          </Text>
        </VStack>

        {showChevron && (
          <HStack
            spacing={1}
            flexShrink={0}
            px={{ base: 2, sm: 3 }}
            py={1.5}
            bg={selectorBg}
            color={selectorColor}
            border="1px solid"
            borderColor={selectorBorderColor}
            borderRadius="full"
          >
            <Text fontSize="xs" fontWeight="bold" whiteSpace="nowrap">
              انتخاب صندوق
            </Text>
            <ChevronDownIcon boxSize={compact ? 5 : 6} />
          </HStack>
        )}
      </HStack>
    </Box>
  );

  if (Array.isArray(fundData)) {
    const options = fundData
      .filter((fund) => !isHiddenFund(fund))
      .map(toOption);
    const selectedOption = options.find((option) => option.value === value);
    const triggerOption = selectedOption || {
      value: "",
      title: placeholder,
      caption: "برای مشاهده صندوق‌ها کلیک کنید",
      fallback: "—",
    };

    return (
      <Menu placement="bottom-end" isLazy>
        <MenuButton
          w="100%"
          textAlign="start"
          borderRadius="xl"
          dir="rtl"
          cursor="pointer"
          transition="transform 0.2s ease, box-shadow 0.2s ease"
          _hover={{
            transform: "translateY(-1px)",
            boxShadow: "md",
          }}
          _focusVisible={{
            boxShadow: `0 0 0 3px ${activeBorderColor}`,
          }}
          aria-label={placeholder}
        >
          {renderCard(triggerOption, Boolean(selectedOption), true, true)}
        </MenuButton>
        <MenuList
          w="var(--popper-reference-width)"
          maxH="min(420px, 60vh)"
          overflowY="auto"
          p={1.5}
          bg={menuBg}
          borderColor={borderColor}
          borderRadius="xl"
          boxShadow="xl"
          zIndex="popover"
          dir="rtl"
          textAlign="right"
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              p={0.5}
              mb={0.5}
              bg="transparent"
              borderRadius="xl"
              dir="rtl"
              textAlign="right"
              _hover={{ bg: menuHoverBg }}
              _focus={{ bg: menuHoverBg }}
              onClick={() => onChange?.(option.value)}
            >
              {renderCard(option, option.value === value, false, true)}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    );
  }

  if (isHiddenFund(fundData)) {
    return null;
  }

  const standaloneCard = renderCard(toOption(fundData), isActive);

  if (onClick) {
    return (
      <Box
        as="button"
        type="button"
        w="100%"
        borderRadius="2xl"
        onClick={onClick}
      >
        {standaloneCard}
      </Box>
    );
  }

  return <Box w="100%">{standaloneCard}</Box>;
};
